/**
 * 节点样式工具函数
 *
 * 提供统一的节点样式生成逻辑
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NodeExecutionStatus } from '../../../types/workflow';

// 合并 Tailwind 类名
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// 节点类型颜色映射（宣纸低饱和度风格）
export const NODE_TYPE_COLORS = {
  start: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: 'text-green-700',
  },
  agent: {
    bg: 'bg-paper-100',
    border: 'border-paper-300',
    text: 'text-paper-800',
    icon: 'text-paper-700',
  },
  loop: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    icon: 'text-purple-700',
  },
  condition: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: 'text-amber-700',
  },
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
    icon: 'text-sky-700',
  },
  note: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
    icon: 'text-yellow-700',
  },
} as const;

export type NodeTypeKey = keyof typeof NODE_TYPE_COLORS;

// 执行状态样式映射
export const EXECUTION_STATUS_STYLES = {
  [NodeExecutionStatus.PENDING]: {
    border: 'border-gray-300',
    animation: '',
    opacity: 'opacity-90',
    shadow: '',
  },
  [NodeExecutionStatus.RUNNING]: {
    border: 'border-paper-500',
    animation: 'animate-node-pulse',
    opacity: '',
    shadow: 'shadow-lg shadow-paper-200',
  },
  [NodeExecutionStatus.COMPLETED]: {
    border: 'border-success-500',
    animation: '',
    opacity: '',
    shadow: '',
  },
  [NodeExecutionStatus.FAILED]: {
    border: 'border-error-500',
    animation: '',
    opacity: '',
    shadow: 'shadow-md shadow-error-100',
  },
  [NodeExecutionStatus.SKIPPED]: {
    border: 'border-gray-300',
    animation: '',
    opacity: 'opacity-60',
    shadow: '',
  },
} as const;

/**
 * 生成节点容器样式类
 *
 * @param nodeType 节点类型
 * @param executionStatus 执行状态
 * @param isSelected 是否选中
 * @param isRunning 是否正在运行
 * @param extraClasses 额外的类名
 */
export function getNodeContainerClasses(
  nodeType: NodeTypeKey,
  executionStatus: NodeExecutionStatus,
  isSelected: boolean = false,
  isRunning: boolean = false,
  extraClasses?: ClassValue[]
): string {
  const typeColors = NODE_TYPE_COLORS[nodeType];
  const statusStyle = EXECUTION_STATUS_STYLES[executionStatus];

  const classes: ClassValue[] = [
    // 基础样式
    'min-w-[180px] max-w-[280px] rounded-lg border-2 bg-white shadow-sm',
    'transition-all duration-150',
    // 选中状态
    isSelected && 'ring-2 ring-paper-500 ring-offset-2',
    // 执行状态边框
    statusStyle.border,
    // 执行状态动画
    isRunning && statusStyle.animation,
    // 执行状态阴影
    isRunning && statusStyle.shadow,
    // 透明度
    executionStatus === NodeExecutionStatus.SKIPPED && statusStyle.opacity,
    // 额外类
    extraClasses,
  ];

  return cn(...classes);
}

/**
 * 生成节点头部样式类
 *
 * @param nodeType 节点类型
 */
export function getNodeHeaderClasses(nodeType: NodeTypeKey): string {
  const colors = NODE_TYPE_COLORS[nodeType];

  return cn(
    'flex items-center gap-2 px-3 py-2 border-b',
    colors.bg,
    colors.border,
    'rounded-t-md',
  );
}

/**
 * 获取节点类型颜色
 *
 * @param nodeType 节点类型
 */
export function getNodeTypeColors(nodeType: NodeTypeKey) {
  return NODE_TYPE_COLORS[nodeType];
}

/**
 * 生成连接点状态样式类
 *
 * @param isRunning 是否执行中
 * @param isCompleted 是否已完成
 * @param isFailed 是否失败
 */
export function getHandleStateClasses(
  isRunning: boolean = false,
  isCompleted: boolean = false,
  isFailed: boolean = false
): string {
  const classes: ClassValue[] = [
    'w-3 h-3 rounded-full border-2 transition-all duration-150 cursor-pointer',
  ];

  if (isFailed) {
    classes.push('bg-error-500 border-error-500');
  } else if (isCompleted) {
    classes.push('bg-success-500 border-success-500');
  } else if (isRunning) {
    classes.push('bg-paper-500 border-paper-500 shadow-lg shadow-paper-200 animate-pulse');
  } else {
    classes.push('bg-white border-gray-300 hover:border-paper-400 hover:bg-paper-50');
  }

  return cn(...classes);
}

export default {
  cn,
  NODE_TYPE_COLORS,
  EXECUTION_STATUS_STYLES,
  getNodeContainerClasses,
  getNodeHeaderClasses,
  getNodeTypeColors,
  getHandleStateClasses,
};
